import { differenceInDays, isFuture } from 'date-fns';
import _, { uniq } from 'lodash';
import { AgreementStatusFilterValue } from '../../components/table/columnFormatHelpers';
import { AgreementStatus, IAgreement, ISanSourceInfo } from '../site/interface';
import { IAgreementStatusesPageState } from '../../pages/adminDashboard/agreementStatuses/AgreementStatusesPage';

export function filterSanSourceInfos(
    sanSourceInfos: ISanSourceInfo[] = [],
    filter: IAgreementStatusesPageState['filter'] = {},
    search: string | null
) {
    let filteredData: ISanSourceInfo[] = sanSourceInfos;

    if (search) {
        filteredData = filteredData.filter(item => {
            return (
                item.siteName.toLowerCase().includes(search) ||
                item.enrollmentId.toLowerCase().includes(search) ||
                item.retailCustomer?.toLowerCase().includes(search) ||
                item.usagePoint?.toLowerCase().includes(search)
            );
        });
    }

    if (filter.companyId) {
        const companyIds = Array.isArray(filter.companyId) ? filter.companyId : [filter.companyId];

        filteredData = filteredData.filter(item => companyIds.includes(item.companyId));
    }

    if (filter.source) {
        const sources = Array.isArray(filter.source) ? filter.source : [filter.source];

        filteredData = filteredData.filter(item => sources.includes(item.source));
    }

    if (filter?.agreementStatus) {
        filteredData = filterByAgreementStatus(filteredData, filter.agreementStatus);
    }

    return filteredData;
}

function filterByAgreementStatus(sanSourceInfos: ISanSourceInfo[] = [], agreementStatus: AgreementStatusFilterValue): ISanSourceInfo[] {
    const currentDate = new Date();
    const DAYS_TO_EXPIRE = 30;

    const activeAgreementSanSourceInfos = getActiveSanSourceInfos(sanSourceInfos);
    const activeEnrollmentIds = uniq(activeAgreementSanSourceInfos.map(item => item.enrollmentId));

    switch (agreementStatus) {
        case 'active':
            return activeAgreementSanSourceInfos;

        case 'inactive':
            return sanSourceInfos.filter(item => !activeEnrollmentIds.includes(item.enrollmentId));

        case 'expired_recently':
            return sanSourceInfos.filter(item => {
                if (!item.agreement?.endAt) return false;

                const endDate = new Date(item.agreement.endAt);
                const daysSinceExpiration = differenceInDays(currentDate, endDate);

                // Check if expired (in the past) AND within last 30 days
                return daysSinceExpiration > 0 && daysSinceExpiration < DAYS_TO_EXPIRE;
            });

        case 'expiring_soon':
            return sanSourceInfos.filter(item => {
                if (!item.agreement?.endAt) return false;

                const endDate = new Date(item.agreement.endAt);
                const daysUntilExpiration = differenceInDays(endDate, currentDate);

                // Check if not yet expired (in the future) AND expires in less than 30 days
                return daysUntilExpiration >= 0 && daysUntilExpiration < DAYS_TO_EXPIRE;
            });

        default:
            return sanSourceInfos;
    }
}

function getActiveSanSourceInfos(sanSourceInfos: ISanSourceInfo[]): ISanSourceInfo[] {
    return sanSourceInfos.filter(item => {
        const agreement: IAgreement = item.agreement || ({} as IAgreement);

        // 1. If agreement has only "datetime" key, return true ("sharepoint" case)
        const hasOnlyDatetime = _.has(agreement, 'datetime') && _.size(agreement) === 1;
        if (hasOnlyDatetime) return true;

        // 2. If agreement has "status" key, check if it's "active"
        const hasActiveStatus = _.has(agreement, 'status') && agreement.status === AgreementStatus.ACTIVE;
        if (!hasActiveStatus) return false;

        // 3. If agreement has "endAt" key, check if it's in the future / or not set
        const hasEndInFuture = (agreement.endAt && isFuture(new Date(agreement.endAt))) || !agreement.endAt;
        if (hasEndInFuture) return true;

        return false;
    });
}
